import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  Icon,
  Link,
  ModalDialogBody,
  ModalDialog,
  ModalDialogContent,
  ModalDialogNav,
  ModalDialogCloseButton,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@vp/swan';
import { useSelector } from 'react-redux';
import { isBoolean, isObject } from 'lodash';
import { debugSelectors } from '../../state/debug';

function Clipboard({ textToCopy }) {
  if (navigator?.clipboard?.writeText) {
    return (
      <Button
        skin="unstyled"
        onClick={() => {
          navigator.clipboard.writeText(textToCopy);
        }}
      >
        <Typography component="span" fontSize="-1" textColor="dark-grey">
          &nbsp;<sup>(copy)</sup>
        </Typography>
      </Button>
    );
  }
  return null;
}
Clipboard.propTypes = {
  textToCopy: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

function Expando({ label, children }) {
  const [expanded, setExpanded] = useState(false);

  return expanded ? (
    <>
      <Button skin="unstyled" onClick={() => setExpanded(false)}>
        {label} -
      </Button>
      <br />
      {children}
    </>
  ) : (
    <Button skin="unstyled" onClick={() => setExpanded(true)}>
      {label} +
    </Button>
  );
}
Expando.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

function stringle(maybeObject) {
  if (isObject(maybeObject)) return JSON.stringify(maybeObject);
  if (isBoolean(maybeObject)) return maybeObject ? 'true' : 'false';
  return maybeObject;
}

export function CartItemDebugDetails({ cartItemId }) {
  const debugInfo = useSelector(state =>
    debugSelectors.selectDebugInfoByItemId(state.debug, cartItemId)
  );

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Link
        render={p => (
          <Button
            skin="link"
            className={p.className}
            onClick={() => setIsOpen(true)}
          >
            {p.children}
          </Button>
        )}
      >
        <Icon iconType="info" skin="brand-blue" />
        <Typography fontSize={1} as="span">
          Debug
        </Typography>
      </Link>
      <ModalDialog
        isOpen={isOpen}
        onRequestDismiss={() => setIsOpen(false)}
        onlyRenderWhenOpen
      >
        <ModalDialogContent>
          <ModalDialogNav>
            <ModalDialogCloseButton accessibleText="Close" />
            {/* Close Button label text must be localized! */}
          </ModalDialogNav>
          <ModalDialogHeader>
            <ModalDialogTitle>
              Item Debug Info <Clipboard textToCopy={stringle(debugInfo)} />
            </ModalDialogTitle>
          </ModalDialogHeader>
          <ModalDialogBody>
            {Object.keys(debugInfo).map(key => (
              <Typography key={key}>
                <Typography fontSize="-1" fontWeight="bold">
                  {key}
                </Typography>
                <Typography fontSize="-1" ml="5">
                  {Object.keys(debugInfo[key])
                    .filter(subKey => debugInfo[key][subKey] !== undefined)
                    .map(subKey => {
                      let subKeyComponent;
                      if (subKey.endsWith('Url')) {
                        subKeyComponent = (
                          <>
                            <a
                              target="_blank"
                              href={debugInfo[key][subKey]}
                              rel="noreferrer"
                            >
                              {subKey}
                            </a>
                            <Clipboard textToCopy={debugInfo[key][subKey]} />
                          </>
                        );
                      } else if (subKey.endsWith('Data')) {
                        subKeyComponent = (
                          <>
                            {subKey}:{' '}
                            <Expando label="JSON">
                              <pre style={{ overflowX: 'scroll' }}>
                                {JSON.stringify(
                                  debugInfo[key][subKey],
                                  null,
                                  2
                                )}
                              </pre>
                            </Expando>
                            <Clipboard
                              textToCopy={JSON.stringify(
                                debugInfo[key][subKey],
                                null,
                                2
                              )}
                            />
                          </>
                        );
                      } else {
                        subKeyComponent = (
                          <>
                            {subKey}: {stringle(debugInfo[key][subKey])}
                            <Clipboard
                              textToCopy={stringle(debugInfo[key][subKey])}
                            />
                          </>
                        );
                      }

                      return <div key={subKey}>{subKeyComponent}</div>;
                    })}
                </Typography>
              </Typography>
            ))}
          </ModalDialogBody>
        </ModalDialogContent>
      </ModalDialog>
    </>
  );
}

CartItemDebugDetails.propTypes = {
  cartItemId: PropTypes.string.isRequired,
};
